/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import logger from 'js/app/loggerSingleton';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { ButtonProps } from '@coursera/cds-core';
import { Button, Dialog, Typography2, breakpoints } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import PrePurchaseUpsellAllPlansModal from 'bundles/coursera-plus-common/components/prePurchaseUpsell/PrePurchaseUpsellAllPlansModal';
import PrePurchaseUpsellEnrollButton from 'bundles/coursera-plus-common/components/prePurchaseUpsell/PrePurchaseUpsellEnrollButton';
import { getS12nEnrollModalCopies } from 'bundles/coursera-plus-common/components/prePurchaseUpsell/PrePurchaseUpsellEnrollCopies';
import {
  useGetIsGatewayCertificate,
  usePrePurchaseUpsellEnrollment,
} from 'bundles/coursera-plus-common/utils/prePurchaseUpsellUtils';
import SubscriptionFooter from 'bundles/enroll/components/subscriptions/SubscriptionFooter';
import usePageData from 'bundles/enroll/data/usePageData';
import { useEnrollModalEventing } from 'bundles/enroll/utils/eventingUtils';
import ReactPriceDisplay from 'bundles/payments-common/components/ReactPriceDisplay';

import _t from 'i18n!nls/coursera-plus';

const styles = {
  dialog: css`
    .cds-Dialog-dialog {
      max-width: 720px !important;
    }
  `,
  header: css`
    padding-bottom: var(--cds-spacing-100);
  `,
  subtitle: css`
    margin-bottom: var(--cds-spacing-400);
  `,
  valueProps: css`
    padding: 0;
    margin: 0;
    list-style-type: none;

    li:first-child {
      padding-top: 0;
    }
  `,
  valuePropListItem: css`
    display: flex;
    align-items: flex-start;
    padding: var(--cds-spacing-100) 0;
  `,
  valuePropHeader: css`
    margin: 0;
  `,
  valuePropSubheader: css`
    padding-top: 4px;
  `,
  checkMark: css`
    flex-shrink: 0;
    margin-right: var(--cds-spacing-100);
  `,
  actions: (onSdp: boolean) => css`
    padding: ${onSdp ? `24px 48px 48px 48px` : `24px 48px 30px 48px`};
  `,
  button: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: 100%;
    }
  `,
};

type Props = {
  handleClose: () => void;
  onSdp: boolean;
  isFromS12nSelection?: boolean;
};
const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const PrePurchaseUpsellS12nEnrollModal = ({ handleClose, onSdp, isFromS12nSelection }: Props) => {
  const [step, setStep] = useState<1 | 2>(1);
  const { s12n, course, s12nDerivatives } = usePageData();
  const { isGatewayCertificate } = useGetIsGatewayCertificate({ slug: s12n?.slug });
  const { enrollInSubscription, canEnrollThroughS12nSubscriptionFreeTrial } = usePrePurchaseUpsellEnrollment({
    isFromS12nSelection,
  });
  const { getEnrollModalTrackingRef, trackEnrollModalClose, trackEnrollModalSecondaryCta } = useEnrollModalEventing({
    isUsingPrePurchaseUpsellFlow: true,
  });

  if (!s12n || !s12nDerivatives || !s12nDerivatives.catalogPrice) {
    logger.error('Unable to render PrePurchase Upsell enroll modal without s12n or pricing');
    return null;
  }

  const { catalogPrice } = s12nDerivatives;
  const visibilityTrackingRef = getEnrollModalTrackingRef();
  const copy = getS12nEnrollModalCopies({
    s12nPrice: s12nDerivatives,
    s12nProductVariant: s12n.productVariant,
    s12n,
    course,
  });

  const { HeadingGroup, Content, Actions } = Dialog;

  const getTitle = () => {
    if (canEnrollThroughS12nSubscriptionFreeTrial) {
      return copy.freeTrialTitle;
    } else {
      return copy.paidTitle;
    }
  };

  const getSubtitle = () => {
    if (onSdp && canEnrollThroughS12nSubscriptionFreeTrial) {
      return null;
    } else if (onSdp && !canEnrollThroughS12nSubscriptionFreeTrial) {
      return copy.paidS12nSubtitle;
    } else if (!onSdp && canEnrollThroughS12nSubscriptionFreeTrial) {
      return copy.freeTrialSubtitle;
    } else {
      return copy.paidCourseSubtitle;
    }
  };

  const onCloseClick = () => {
    handleClose();
    trackEnrollModalClose();
  };

  const getValueProps = () => {
    if (canEnrollThroughS12nSubscriptionFreeTrial) {
      const valueProps = copy.freeTrialValueProps.FREE_TRIAL_LOGGED_IN_BULLETS;
      return (
        <ul css={styles.valueProps}>
          {valueProps.map((valueProp) => (
            <li key={`value-prop-${valueProp}`} css={styles.valuePropListItem}>
              <CheckIcon size="large" color="interactive" css={styles.checkMark} />
              <Typography2 component="div">
                <Typography2 component="div" variant="subtitleMedium" css={styles.valuePropHeader}>
                  <FormattedMessage
                    message={valueProp.header}
                    monthlyPrice={
                      <ReactPriceDisplay
                        value={catalogPrice.amount}
                        currency={catalogPrice.currencyCode}
                        hideCurrencyCode={true}
                      />
                    }
                  />
                </Typography2>
                <Typography2 component="div" variant="bodySecondary" css={styles.valuePropSubheader}>
                  {valueProp.subheader}
                </Typography2>
              </Typography2>
            </li>
          ))}
        </ul>
      );
    } else {
      const valueProps = copy.paidValueProps;
      return (
        <ul css={styles.valueProps}>
          {valueProps.map((valueProp) => (
            <li key={`value-prop-${valueProp}`} css={styles.valuePropListItem}>
              <CheckIcon size="large" color="interactive" css={styles.checkMark} />
              <Typography2 component="p">{valueProp}</Typography2>
            </li>
          ))}
        </ul>
      );
    }
  };

  const showViewAllPlans = () => {
    setStep(2);
    trackEnrollModalSecondaryCta('view_all_plans');
  };

  const ctaCopy = canEnrollThroughS12nSubscriptionFreeTrial ? copy.freeTrialCta : copy.paidCta;

  if (step === 2) {
    return <PrePurchaseUpsellAllPlansModal isGatewayCertificate={isGatewayCertificate} onClose={() => setStep(1)} />;
  }

  return (
    <Dialog
      data-testid="pre-purchase-upsell-s12n-enroll-modal"
      open
      aria-describedby="dialog-content"
      width="medium"
      variant="standard"
      ref={visibilityTrackingRef}
      sticky="actions"
      onClose={onCloseClick}
    >
      <HeadingGroup css={styles.header}>{getTitle() as string}</HeadingGroup>
      <Content id="dialog-content">
        <Typography2 variant="bodyPrimary" component="div" css={styles.subtitle}>
          <Typography2 variant="subtitleMedium" component="p">
            {getSubtitle()}
          </Typography2>
        </Typography2>
        {getValueProps()}
      </Content>
      <Actions css={styles.actions(onSdp)}>
        <PrePurchaseUpsellEnrollButton
          trackingName="pre_purchase_upsell_s12n_enroll_cta"
          data-testid="pre-purchase-upsell-s12n-enroll-cta"
          variant="primary"
          onCtaClick={enrollInSubscription}
          css={styles.button}
        >
          {ctaCopy}
        </PrePurchaseUpsellEnrollButton>
        <TrackedButton
          trackingName="pre_purchase_upsell_s12n_enroll_modal_view_all_plans_cta"
          data-testid="pre-purchase-upsell-s12n-enroll-modal-view-all-plans-cta"
          css={[
            styles.button,
            css`
              ${breakpoints.up('sm')} {
                margin-left: var(--cds-spacing-200);
              }
              ${breakpoints.down('xs')} {
                margin-top: var(--cds-spacing-100);
              }
            `,
          ]}
          size="medium"
          variant="secondary"
          onClick={showViewAllPlans}
        >
          {copy.secondaryCta}
        </TrackedButton>
        {!onSdp && (
          <div>
            <SubscriptionFooter message={_t('{audit} this course')} />
          </div>
        )}
      </Actions>
    </Dialog>
  );
};
export default PrePurchaseUpsellS12nEnrollModal;
